/**
 * ProTopHeader
 * ============
 * Top header bar for the Professional Portal.
 *
 * Mirrors the SRL-side TopHeader (notifications bell, help, settings, user menu)
 * while routing links to the Pro portal equivalents.
 *
 * Intentionally has NO "Dashboard" label in the bar — the Pro portal is a
 * full application, not a single page, so the wordmark+badge serves as brand
 * identity instead of a page title.
 */

import { Link, useNavigate } from 'react-router-dom';
import { HelpCircle, Settings, User, LogOut, Menu } from 'lucide-react';
import { useState, useRef, useEffect } from 'react';
import { useAuthState, useAuthActions } from '@/auth/AuthProvider';
import BrandWordmark from '@/components/brand/BrandWordmark';
import NotificationBell from '@/components/notifications/NotificationBell';
import { useUnifiedNotificationCount } from '@/hooks/useUnifiedNotificationCount';

interface ProTopHeaderProps {
  onMobileMenuToggle?: () => void;
}

export default function ProTopHeader({ onMobileMenuToggle }: ProTopHeaderProps) {
  const { user } = useAuthState();
  const { signOutUser } = useAuthActions();
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { totalUnread } = useUnifiedNotificationCount({});

  const handleSignOut = async () => {
    try {
      await signOutUser();
      navigate('/login');
    } catch (error) {
      console.error('[ProTopHeader] Sign out error:', error);
    }
  };

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setIsUserMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <header className="sticky top-0 z-20 flex h-16 items-center justify-between border-b border-slate-800 bg-slate-900/95 backdrop-blur-sm px-4 sm:px-6 shadow-sm">
      {/* Left: Mobile hamburger + Wordmark */}
      <div className="flex items-center gap-3">
        {/* Mobile sidebar toggle */}
        {onMobileMenuToggle && (
          <button
            type="button"
            onClick={onMobileMenuToggle}
            className="lg:hidden rounded-lg p-2 text-slate-300 hover:bg-slate-800 hover:text-white transition focus:outline-none focus:ring-2 focus:ring-orange-600"
            aria-label="Open navigation menu"
          >
            <Menu size={20} />
          </button>
        )}

        {/* Brand — links back to dashboard without labelling the bar "Dashboard" */}
        <Link
          to="/pro/dashboard"
          className="flex items-center gap-2 transition hover:opacity-80"
          aria-label="Professional portal home"
        >
          <BrandWordmark darkText={false} className="h-8" />
          <span className="rounded-md bg-purple-900/50 px-2 py-0.5 text-xs font-bold text-purple-300 border border-purple-700/30 hidden sm:inline">
            Pro
          </span>
        </Link>
        <span className="hidden sm:inline text-slate-500 select-none">|</span>
        <span className="hidden sm:inline text-sm font-medium text-slate-400">Attorney Portal</span>
      </div>

      {/* Right: Help, Settings, Notifications, User menu */}
      <div className="flex items-center gap-2 sm:gap-3">
        {/* Help */}
        <Link
          to="/pro/help"
          className="rounded-lg border border-slate-700 bg-slate-800 p-2 text-slate-300 transition hover:bg-slate-700 hover:text-slate-100 focus:outline-none focus:ring-2 focus:ring-orange-600"
          aria-label="Help Center"
          title="Help Center"
        >
          <HelpCircle size={18} />
        </Link>

        {/* Settings / Profile Setup */}
        <Link
          to="/pro/setup"
          className="rounded-lg border border-slate-700 bg-slate-800 p-2 text-slate-300 transition hover:bg-slate-700 hover:text-slate-100 focus:outline-none focus:ring-2 focus:ring-orange-600"
          aria-label="Profile & Settings"
          title="Profile & Settings"
        >
          <Settings size={18} />
        </Link>

        {/* Notification Bell */}
        <NotificationBell totalUnread={totalUnread} userRole="pro" />

        {/* User Menu */}
        <div className="relative" ref={userMenuRef} data-testid="pro-user-menu">
          <button
            type="button"
            onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
            className="rounded-lg border border-slate-700 bg-slate-800 p-2 text-slate-300 transition hover:bg-slate-700 hover:text-slate-100 focus:outline-none focus:ring-2 focus:ring-orange-600"
            aria-expanded={isUserMenuOpen}
            aria-label="Account Menu"
            data-testid="user-menu-trigger"
          >
            <User size={18} />
          </button>

          {isUserMenuOpen && (
            <div className="absolute right-0 top-full mt-2 w-56 rounded-lg border border-slate-700 bg-slate-900 shadow-xl z-50">
              <div className="border-b border-slate-700 px-4 py-3">
                <p className="text-xs font-medium text-slate-400">Signed in as</p>
                <p className="mt-0.5 truncate text-sm font-medium text-slate-100">
                  {user?.email ?? 'Professional'}
                </p>
              </div>
              <div className="p-2">
                <Link
                  to="/pro/setup"
                  onClick={() => setIsUserMenuOpen(false)}
                  className="flex w-full items-center gap-3 rounded-md px-3 py-2 text-left text-sm text-slate-300 transition hover:bg-slate-800"
                >
                  <Settings size={16} />
                  <span>Profile Setup</span>
                </Link>
                <Link
                  to="/pro/billing"
                  onClick={() => setIsUserMenuOpen(false)}
                  className="flex w-full items-center gap-3 rounded-md px-3 py-2 text-left text-sm text-slate-300 transition hover:bg-slate-800"
                >
                  <User size={16} />
                  <span>Billing</span>
                </Link>
                <Link
                  to="/pro/help"
                  onClick={() => setIsUserMenuOpen(false)}
                  className="flex w-full items-center gap-3 rounded-md px-3 py-2 text-left text-sm text-slate-300 transition hover:bg-slate-800"
                >
                  <HelpCircle size={16} />
                  <span>Help Center</span>
                </Link>
                <button
                  type="button"
                  onClick={handleSignOut}
                  className="flex w-full items-center gap-3 rounded-md px-3 py-2 text-left text-sm text-red-400 transition hover:bg-red-950/50"
                >
                  <LogOut size={16} />
                  <span>Sign Out</span>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
